import { Link } from "react-router-dom";
import { SERVICES } from "../../../mocks/businessData";
import { useInView } from "../../../hooks/useInView";

export default function ServicesOverview() {
  const { ref: sectionRef, inView } = useInView(0.08);
  const featured = SERVICES[0];
  const rest = SERVICES.slice(1);

  return (
    <section ref={sectionRef} className="bg-white py-20 md:py-28">
      <div className="w-full px-4 md:px-8 lg:px-16">

        {/* Section header */}
        <div className={`flex flex-col md:flex-row md:items-end justify-between mb-14 gap-6 reveal ${inView ? "visible" : ""}`}>
          <div>
            <span className="font-sans text-xs font-semibold tracking-[0.22em] uppercase text-rust-600 mb-3 block">
              What We Build
            </span>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-charcoal leading-tight max-w-2xl">
              Complete Landscape Design &amp; Construction Services
            </h2>
          </div>
          <div className="flex-shrink-0">
            <Link
              to="/services"
              className="whitespace-nowrap inline-flex items-center gap-2 font-sans text-sm font-semibold text-rust-700 hover:text-rust-800 border border-rust-200 hover:border-rust-500 px-5 py-2.5 rounded-sm transition-all cursor-pointer"
            >
              All Services
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        {/* Featured service — editorial split */}
        <div
          className={`grid grid-cols-1 lg:grid-cols-5 rounded-xl overflow-hidden border border-stone-100 mb-6 reveal ${inView ? "visible" : ""} reveal-delay-1`}
        >
          {/* Image: 3 of 5 */}
          <div className="lg:col-span-3 relative h-72 lg:h-[480px] overflow-hidden">
            <img
              src={featured.image}
              alt={featured.name}
              className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/20"></div>
            {/* Service type tag */}
            <div className="absolute top-5 left-5 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1.5">
              <span className="font-sans text-xs font-semibold text-rust-700">Featured Service</span>
            </div>
          </div>

          {/* Content: 2 of 5 */}
          <div className="lg:col-span-2 bg-white p-8 md:p-10 lg:p-12 flex flex-col justify-center">
            <span className="font-sans text-xs font-semibold uppercase tracking-[0.2em] text-rust-600 mb-3 block">
              {featured.tagline}
            </span>
            <h3 className="font-serif text-2xl md:text-3xl font-bold text-charcoal leading-tight mb-4">
              {featured.name}
            </h3>
            <p className="font-sans text-sm text-stone-500 leading-relaxed mb-7">
              {featured.description}
            </p>
            <ul className="space-y-2.5 mb-8">
              {featured.features.map((f) => (
                <li key={f} className="flex items-center gap-3">
                  <i className="ri-checkbox-circle-fill text-rust-600 text-sm flex-shrink-0"></i>
                  <span className="font-sans text-sm text-charcoal">{f}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/services"
              className="whitespace-nowrap inline-flex items-center gap-2 bg-charcoal hover:bg-charcoal-800 text-white font-sans text-sm font-semibold px-6 py-3.5 rounded-sm transition-all cursor-pointer self-start"
            >
              Learn More
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        {/* Remaining three services */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-16">
          {rest.map((service, i) => (
            <Link
              key={service.id}
              to={service.href}
              className={`group block bg-stone-50 hover:bg-white border border-stone-100 hover:border-rust-200 rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:-translate-y-1 reveal ${inView ? "visible" : ""} reveal-delay-${i + 2}`}
            >
              {/* Image */}
              <div className="w-full h-52 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.name}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-600"
                />
              </div>

              {/* Content */}
              <div className="p-6">
                <span className="font-sans text-[10px] font-bold uppercase tracking-[0.18em] text-rust-600 mb-2 block">
                  {service.tagline}
                </span>
                <h3 className="font-serif text-lg font-bold text-charcoal mb-2.5 leading-snug">
                  {service.name}
                </h3>
                <p className="font-sans text-sm text-stone-500 leading-relaxed mb-4 line-clamp-3">
                  {service.description}
                </p>
                <span className="font-sans text-xs font-semibold text-rust-700 flex items-center gap-1.5 group-hover:gap-2.5 transition-all duration-200">
                  Explore service <i className="ri-arrow-right-line"></i>
                </span>
              </div>
            </Link>
          ))}
        </div>

        {/* Mid-page CTA strip */}
        <div className={`bg-charcoal rounded-xl p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6 reveal ${inView ? "visible" : ""} reveal-delay-5`}>
          <div className="text-center md:text-left">
            <p className="font-sans text-xs text-stone-400 mb-1 tracking-wide uppercase">Ready to get started?</p>
            <h3 className="font-serif text-2xl md:text-3xl font-bold text-white">
              Let&rsquo;s build your dream outdoor space.
            </h3>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <Link
              to="/#contact"
              onClick={() => {
                setTimeout(() => {
                  document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
                }, 100);
              }}
              className="whitespace-nowrap bg-rust-700 hover:bg-rust-800 text-white font-sans text-sm font-semibold px-8 py-3.5 rounded-sm transition-colors cursor-pointer"
            >
              Begin Your Project
            </Link>
            <a
              href="tel:5194738111"
              className="whitespace-nowrap border border-stone-600 hover:border-stone-400 text-stone-300 hover:text-white font-sans text-sm font-semibold px-8 py-3.5 rounded-sm transition-colors cursor-pointer flex items-center justify-center gap-2"
            >
              <i className="ri-phone-fill text-rust-400"></i>
              519-473-8111
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
